import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { CreateContactPoint } from '../../../api/app-types/relative.app';
import { UpdateUser, User } from '../../../api/app-types/user.app';
import { SearchService } from '../../../api/services/search.service';
import { USER_SERVICE } from '../../../api/services/user.service';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { hasError } from '../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { userExistsValidator } from '../../../shared/validators/user-exist-validator';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { matDateProviders } from '../../../shared/helpers/date.func';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';
import { CurrentUserService } from '../../../core/services/current-user.service';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';

@Component({
	selector: 'lvr-user-details-edit',
	imports: [
		MatFormFieldModule,
		MatSelectModule,
		MatTooltipModule,
		TranslocoPipe,
		MatButtonModule,
		ReactiveFormsModule,
		MatRadioModule,
		MatInputModule,
		LvrInputDirective,
		LvrAutoSaveDirective,
		LvrAppendAsteriskPipe,
		LvrPaddingDirective,
		AddressCompletion,
		LvrField,
		MatDatepickerModule,
		LvrRestoreActions,
		LvrContactPoint,
		MatIcon,
		MatIconButton,
		MatCheckbox,
		LvrDefaultActions,
		LvrButton,
	],
	templateUrl: './user-details-edit.html',
	styleUrl: './user-details-edit.scss',
	providers: [matDateProviders, SearchService],
})
export class UserDetailsEdit extends RouteInjection implements OnInit {
	private readonly env = inject(ENVIRONMENT);
	private readonly routeData = toSignal(this.route.data);
	private readonly draftService = inject(FormPersistenceService);
	private readonly searchApi = inject(SearchService);
	private readonly transloco = inject(TranslocoService);
	private readonly userService = inject(USER_SERVICE);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly currentUserService = inject(CurrentUserService);
	private readonly titleRegex = new RegExp(this.env.titleRegexStr);
	private readonly nameRegex = new RegExp(this.env.nameRegexStr);
	public readonly user = computed(() => this.routeData()?.['user'] as User);
	public readonly form = this.initUpdateUserForm();
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly hasError = hasError;
	public readonly loading = signal(false);

	public readonly draftId = FORM_KEYS.UPDATE_USER;
	public readonly availableGenders = computed(() => this.routeData()?.['genders'] ?? []);
	private readonly contactPointCategoriesData =
		this.resolveDataAsSignal<PaginatedResponse<ContactPointCategory>>('contactPointCategories');
	public readonly contactPointCategories = computed<ContactPointCategory[]>(
		() => this.contactPointCategoriesData()?.entities ?? [],
	);
	public get contactPoints(): ContactPoint[] {
		return (this.form.get('baseInfo.contactPoints')?.value ?? []) as ContactPoint[];
	}

	public async ngOnInit() {
		this.initializeFormFromUserData();

		const u = this.user();
		const emailCtrl = this.form.get('baseInfo.businessEmail');

		// Check if this email is already used by a different user and exclude current user via its ObjectId
		emailCtrl?.setAsyncValidators(
			userExistsValidator(this.searchApi, { originalEmail: u?.email, excludeObjectId: u?.id }),
		);
		emailCtrl?.updateValueAndValidity({ emitEvent: false });
	}

	public addContactPoint() {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const cp: ContactPoint = {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
		values.push(cp);
		this.form.get('baseInfo.contactPoints')?.patchValue(values);
	}

	public removeContactPoint(index: number) {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index !== -1) {
			values.splice(index, 1);
			this.form.get('baseInfo.contactPoints')?.patchValue(values);
		}
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('baseInfo.contactPoints');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		if (index >= 0 && index < values.length) {
			Object.assign(values[index], data.value); // Update same item in array
		} else {
			values.push(data.value); // Add if new
		}
	}

	/**
	 * Saves the updated client data.
	 * Shows a success or error notification depending on the result.
	 */
	public async onSave() {
		if (this.loading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const user = this.user();
		if (!user) {
			console.warn('No user available');
			return;
		}

		this.loading.set(true);

		const fv = this.form.value;

		// Handles leading and trailing whitespaces in string form controls
		Object.keys(fv).forEach((k) => {
			if (typeof fv[k] === 'string') fv[k] = fv[k].trim();
		});

		const body: UpdateUser = {
			address: {
				postalCodeAndCity: fv.baseInfo?.address?.['postalCodeAndCity'] ?? '',
				streetAndHouseNumber: fv.baseInfo?.address?.['streetAndHouseNumber'] ?? '',
			},
			title: fv.baseInfo?.title ?? '',
			firstName: fv.baseInfo?.firstName ?? '',
			notesOnAccessibility: fv.baseInfo?.notesOnAccessibility ?? '',
			remark: fv.baseInfo?.remark ?? '',
			genderId: fv.baseInfo?.genderId ?? undefined,
			employmentId: user.employment?.id ?? -1,
			positionShare: user.positionShare?.share ?? -1,
			roleId: user.roles?.id ?? '',
			specializationId: fv.employment?.serviceId ?? -1,
			start: '',
			contactPoints: [],
			isSeverelyDisabled: fv.baseInfo.isSeverelyDisabled,
			integrationServiceOfficeId: this.user().integrationServiceOffice!.id,
		};

		const filteredCps = (fv.baseInfo.contactPoints ?? []).filter(isContactPointComplete);

		if (filteredCps.length > 0) {
			const additionalCps: CreateContactPoint[] = filteredCps.map((cp: CreateContactPoint) => ({
				id: cp.id === -1 ? undefined : cp.id,
				preferredWayOfContact: cp.preferredWayOfContact,
				categoryContactPointId: cp.categoryContactPointId,
				contactValue: cp.contactValue?.trim(),
				remark: cp.remark?.trim(),
			}));

			body.contactPoints!.push(...additionalCps);
		}

		try {
			await this.userService.updateMe(body);
			await this.currentUserService.updateCurrentUser();
			this.draftService.deleteDraft('user', this.draftId);
			await this.router.navigate(['..'], { relativeTo: this.route });
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.changes_saved')),
			);
		} catch {
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.changes_save_error')),
			);
		} finally {
			this.loading.set(false);
		}
	}

	/**
	 * Cancels the edit mode and discards any unsaved changes.
	 * Deletes the saved draft and emits a cancel event to the parent.
	 */
	public handleCancelEdit() {
		const currentUser = this.user();
		if (!currentUser) {
			return;
		}

		this.draftService.deleteDraft('user', this.draftId);
		this.router.navigate(['..'], { relativeTo: this.route });
	}

	/**
	 * Initializes the user form using either a saved draft (if available)
	 * or the user data provided via API.
	 */
	private initializeFormFromUserData() {
		const user = this.user();
		if (!user) {
			console.warn('No user available');
			return;
		}

		this.form.patchValue(this.mapUserToForm(user));
	}

	/**
	 * Maps the current `User` object to the form structure.
	 */
	private mapUserToForm(u: User) {
		return {
			baseInfo: {
				title: u.title,
				firstName: u.firstName ?? '',
				name: u.lastName ?? '',
				notesOnAccessibility: u.notesOnAccessability ?? '',
				remark: '', // 'remark' not available in the User GET yet
				businessEmail: u.email ?? '',
				genderId: u.genderId,
				address: {
					streetAndHouseNumber: u.streetAddress ?? '',
					postalCodeAndCity: this.joinPostalCity(u.postalCode, u.city),
				},
				contactPoints: u.contactPoints,
				isSeverelyDisabled: u.isSeverelyDisabled,
			},
		};
	}

	/**
	 * Joins postal code and city into a single line.
	 */
	private joinPostalCity = (postal?: string, city?: string) =>
		[postal ?? '', city ?? ''].filter(Boolean).join(' ') || '';

	/**
	 * Initializes the form for updating a user.
	 */
	private initUpdateUserForm(): FormGroup {
		const fb = inject(FormBuilder);
		return fb.group({
			baseInfo: fb.group({
				title: ['', [Validators.pattern(this.titleRegex)]],
				firstName: ['', [Validators.required, Validators.pattern(this.nameRegex)]],
				notesOnAccessibility: [''],
				remark: ['', [Validators.maxLength(200)]],
				genderId: fb.control<number | undefined>(undefined, [Validators.required]),
				address: [undefined],
				contactPoints: [[] as ContactPoint[]],
				isSeverelyDisabled: [false],
			}),
		});
	}
}
